import { useEffect, useState } from 'react';

import ArtistIcon from '@/assets/all-icons/artist-icon';
import config from "@/config";
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useQuery from "@/hooks/useQuery";
import useTranslations from '@/i18n/useTranslations';
import useSearchRandomizedFeed from '@/pages/search/hooks/useSearchRandomizedFeed';
import fetchWrapper from '@/services/fetchWrapper.service';
import HomeHeader from '@/shared-components/home/components/HomeHeader';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrSearchLandingBootstrap } from '@/utils/ssrSearchLandingBootstrap';

import RandomizedHomeLinksList from '../components/RandomizedHomeLinksList';
import useRandomizedHomeLinks from '../hooks/useRandomizedHomeLinks';

import VirtualList from "./components/VirtualList";

import './index.css';

const RANDOM_LIMIT = 32;
const RANDOM_ENDPOINT = `${config.apis.api.url}/home/links/randomized/artists`;

function readInitialSearchLandingSeoPayload() {
  const payload = getSsrSearchLandingBootstrap('artists');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

function HomeArtistsRandomized() {
  const tSearchCategories = useTranslations('common.shared.search.categories');
  const [searchSeo, setSearchSeo] = useState(() => readInitialSearchLandingSeoPayload());
  const randomizedState = useRandomizedHomeLinks({
    endpoint: RANDOM_ENDPOINT,
    limitParam: 'artist_limit',
    limit: RANDOM_LIMIT,
    errorMessage: 'Unable to refresh artists at the moment.',
  });

  useEffect(() => {
    let ignore = false;

    async function loadSearchSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/search/artists`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setSearchSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load artists SEO payload', error);
        }
      }
    }

    loadSearchSeo();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <StaticSeoHead
        title={searchSeo?.title || "Contemporary Artists | Suarte"}
        description={searchSeo?.metaDescription || "Discover contemporary and modern artists on Suarte. Explore profiles, original artworks, and paintings by gallery-represented talent from around the world."}
        canonicalUrl={searchSeo?.canonicalUrl}
        canonicalPath={searchSeo?.canonicalPath || "/artists"}
        robots={searchSeo?.robots}
        ogType={searchSeo?.ogType}
        image={searchSeo?.ogImage}
        structuredData={searchSeo?.structuredData}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Artists', url: 'https://suarte.art/artists' },
        ]}
      />
      <AppNavigationPage>
        <RandomizedHomeLinksList
          className="home-artists__page home-artists__randomized"
          randomizedState={randomizedState}
          emptyText="No artists available at the moment."
          emptyTextClassName="home-artists__empty-text font-size-medium"
          VirtualListComponent={VirtualList}
          virtualListProps={{ showSearchCTA: true }}
        >
          <HomeHeader
            onInput={() => {}}
            value=""
            icon={<ArtistIcon className="home-artists__header-icon" />}
            text={tSearchCategories('artists')}
            placeholder="Search for artists..."
            hideSearch
          />
        </RandomizedHomeLinksList>
      </AppNavigationPage>
    </>
  );
}

function HomeArtistsSearch({ valueFromSearch = "", fromSearch = false }) {
  const [params, setParams] = useGetSearchParams();
  const [query, setQuery] = useState(params ? params : { q: "" });
  const searchValue = typeof valueFromSearch?.q === "string" ? valueFromSearch.q.trim() : "";
  const shouldUseRandomizedResults = searchValue.length === 0;
  const randomizedState = useSearchRandomizedFeed({
    feedKey: 'artists',
    endpoint: RANDOM_ENDPOINT,
    limitParam: 'artist_limit',
    limit: RANDOM_LIMIT,
    enabled: shouldUseRandomizedResults,
    errorMessage: 'Unable to refresh artists at the moment.',
  });
  const { loading, queryData, loadMoreData } = useQuery("home_artists", `${config.apis.api.url}/search/artist`, query, {
    optionalToken: true,
    customQueryValidation: (nextQuery) => Boolean(nextQuery?.q && nextQuery.q.trim()),
  });

  function onInput(value) {
    const newQuery = { q: typeof value === "string" ? value : "" };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  useEffect(() => {
    onInput(searchValue);
  }, [searchValue]);

  if (shouldUseRandomizedResults) {
    return (
      <RandomizedHomeLinksList
        className="home-artists__page"
        randomizedState={randomizedState}
        emptyText="No artists available at the moment."
        emptyTextClassName="home-artists__empty-text font-size-medium"
        VirtualListComponent={VirtualList}
        virtualListProps={{ fromSearch }}
      />
    );
  }

  return (
    <div className="home-artists__page">
      {loading ? (
        <Loader />
      ) : queryData.data.data.length === 0 ? (
        <Text className="home-artists__empty-text font-size-medium" paragraph>
          Represented artists will be added soon!
        </Text>
      ) : (
        <VirtualList
          key={queryData.queryString}
          items={queryData.data}
          onLoadMore={loadMoreData}
          fromSearch={fromSearch}
        />
      )}
    </div>
  );
}

export default function HomeArtists(props) {
  if (props.fromSearch) {
    return <HomeArtistsSearch {...props} />;
  }
  return <HomeArtistsRandomized />;
}
